import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Check, Sparkles, Crown, ArrowRight, Loader2, Info, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { billingApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const TIER_ORDER = ["free", "pro", "premium"];
const TIER_RANK = { free: 0, pro: 1, premium: 2 };

const PlanCard = ({ tier, price, featured, current, onSelect, ctaLabel, ctaDisabled, loading }) => {
    const { t } = useTranslation();
    const meta = t(`pricing.tiers.${tier}`);
    const Icon = tier === "premium" ? Crown : tier === "pro" ? Sparkles : GraduationCap;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`pricing-card-${tier}`}
            className={cn(
                "relative flex flex-col rounded-2xl border bg-surface-1 p-6 shadow-elev-1",
                featured ? "border-[hsl(var(--primary))]/55 ring-1 ring-[hsl(var(--primary))]/30 lg:scale-[1.02]" : "border-border"
            )}
        >
            {featured && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary-foreground))]">
                    <Sparkles className="h-3 w-3" /> {t("pricing.mostPopular")}
                </span>
            )}
            <div className="flex items-center gap-2.5">
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl", featured ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "bg-surface-3 text-t2")}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="font-sans-display text-lg font-semibold text-t1">{meta.name}</div>
                    <div className="text-xs text-t4">{meta.tagline}</div>
                </div>
            </div>

            <div className="mt-5 flex items-end gap-1">
                <span className="font-sans-display text-4xl font-semibold tracking-tight text-t1">{price === 0 ? t("pricing.free") : `${price} €`}</span>
                {price > 0 && <span className="mb-1.5 text-sm text-t4">{t("pricing.perMonth")}</span>}
            </div>

            {tier === "premium" && (
                <div className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-[11px] text-t3">
                    <Crown className="h-3 w-3 text-[hsl(var(--primary))]" /> {t("pricing.plusPrevious")}
                </div>
            )}

            <ul className="mt-5 flex-1 space-y-2.5">
                {meta.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-t2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--success))]" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <Button
                data-testid={`pricing-cta-${tier}`}
                onClick={onSelect}
                disabled={ctaDisabled || loading}
                variant={featured ? "default" : "secondary"}
                className={cn("mt-6 w-full gap-2", !featured && "bg-surface-3/70")}
            >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                        {ctaLabel}
                        {!current && !ctaDisabled && <ArrowRight className="h-4 w-4" />}
                    </>
                )}
            </Button>
            {price > 0 && !current && (
                <p className="mt-2 text-center text-[11px] text-t4">{t("pricing.cancelAnytime")}</p>
            )}
        </motion.div>
    );
};

export default function Pricing() {
    const { user, refresh } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [plans, setPlans] = useState(null);
    const [configured, setConfigured] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await billingApi.plans();
                setPlans(data);
                setConfigured(!!data.configured);
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const pollStatus = useCallback(async (sessionId, attempt = 0) => {
        if (attempt > 6) { setConfirming(false); return; }
        try {
            const res = await billingApi.checkoutStatus(sessionId);
            if (res.payment_status === "paid") {
                toast.success(t("pricing.states.success"));
                if (refresh) await refresh();
                setConfirming(false);
                return;
            }
            if (res.status === "expired" || res.status === "unconfigured") { setConfirming(false); return; }
            setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
        } catch (e) {
            setConfirming(false);
        }
    }, [refresh, t]);

    // Handle Stripe return (success polling / cancel notice) and clean the URL.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sid = params.get("session_id");
        const checkout = params.get("checkout");
        if (sid) {
            setConfirming(true);
            pollStatus(sid);
            window.history.replaceState({}, "", "/pricing");
        } else if (checkout === "cancelled") {
            toast.info(t("pricing.states.cancelled"));
            window.history.replaceState({}, "", "/pricing");
        }
    }, [location.search, pollStatus, t]);

    const priceFor = (tier) => {
        const p = plans?.plans?.find((x) => x.key === tier);
        return p ? p.price_eur : (tier === "pro" ? 9 : tier === "premium" ? 19 : 0);
    };

    const currentTier = (user?.tier || "free").toLowerCase();

    const handleSelect = async (tier) => {
        if (tier === "free") {
            if (!user) navigate("/register");
            return;
        }
        if (!user) { navigate("/login"); return; }
        if (!configured) {
            toast.info(t("pricing.notConfigured.title"), { description: t("pricing.notConfigured.body") });
            return;
        }
        setCheckoutLoading(tier);
        try {
            const res = await billingApi.checkout(tier, window.location.origin);
            if (res.configured && res.url) {
                window.location.href = res.url;
            } else {
                toast.info(t("pricing.notConfigured.title"), { description: t("pricing.notConfigured.body") });
            }
        } catch (e) {
            toast.error(t("pricing.notConfigured.title"));
        } finally {
            setCheckoutLoading(null);
        }
    };

    const ctaFor = (tier) => {
        const current = user && currentTier === tier;
        if (current) return { label: t("pricing.cta.current"), disabled: true, current: true };
        if (tier === "free") return { label: t("pricing.cta.startFree"), disabled: !!user, current: false };
        if (!user) return { label: t("pricing.cta.signInToUpgrade"), disabled: false, current: false };
        // Don't let a premium user "upgrade" to pro.
        if (TIER_RANK[currentTier] > TIER_RANK[tier]) return { label: t("pricing.cta.current"), disabled: true, current: true };
        return { label: tier === "pro" ? t("pricing.cta.upgradePro") : t("pricing.cta.upgradePremium"), disabled: false, current: false };
    };

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-surface-0">
            {/* Top bar */}
            <header className="sticky top-0 z-40 border-b border-border bg-surface-0/80 backdrop-blur supports-[backdrop-filter]:bg-surface-0/60">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
                    <Link to={user ? "/library" : "/"} className="flex items-center gap-2" data-testid="pricing-brand-link">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                            <GraduationCap className="h-5 w-5" />
                        </div>
                        <span className="font-sans-display text-sm font-semibold text-t1">LearnOS</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher testId="language-switcher-pricing" />
                        {user ? (
                            <Button data-testid="pricing-back-to-app" variant="secondary" size="sm" className="gap-1.5 bg-surface-2" onClick={() => navigate("/library")}>
                                <ArrowLeft className="h-3.5 w-3.5" /> {t("pricing.backToApp")}
                            </Button>
                        ) : (
                            <>
                                <Button data-testid="pricing-sign-in" variant="ghost" size="sm" onClick={() => navigate("/login")}>{t("common.signIn")}</Button>
                                <Button data-testid="pricing-start-free" size="sm" onClick={() => navigate("/register")}>{t("common.startFree")}</Button>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
                <div className="max-w-2xl">
                    <span className="mono-label text-t4">{t("pricing.badge")}</span>
                    <h1 className="mt-2 font-sans-display text-3xl font-semibold tracking-tight text-t1 sm:text-4xl">{t("pricing.title")}</h1>
                    <p className="mt-3 text-base text-t2">{t("pricing.subtitle")}</p>
                </div>

                {/* Billing-not-configured notice */}
                {plans && !configured && (
                    <div data-testid="pricing-not-configured-banner" className="mt-6 flex items-start gap-3 rounded-xl border border-[hsl(var(--warning))]/35 bg-[hsl(var(--warning))]/10 px-4 py-3">
                        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
                        <div>
                            <div className="text-sm font-medium text-t1">{t("pricing.notConfigured.title")}</div>
                            <div className="text-xs text-t3">{t("pricing.notConfigured.body")}</div>
                        </div>
                    </div>
                )}

                {confirming && (
                    <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-surface-1 px-4 py-3 text-sm text-t2" data-testid="pricing-confirming">
                        <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" /> {t("pricing.states.processing")}
                    </div>
                )}

                <div className="mt-10 grid gap-6 lg:grid-cols-3">
                    {TIER_ORDER.map((tier) => {
                        const cta = ctaFor(tier);
                        return (
                            <PlanCard
                                key={tier}
                                tier={tier}
                                price={priceFor(tier)}
                                featured={tier === "pro"}
                                current={cta.current}
                                ctaLabel={cta.label}
                                ctaDisabled={cta.disabled}
                                loading={checkoutLoading === tier}
                                onSelect={() => handleSelect(tier)}
                            />
                        );
                    })}
                </div>
            </main>
        </div>
    );
}
